export const ROLE_OPTIONS = [
  "Standard Animator",
  "High-Quality Animator",
  "Script Writer",
  "Video Editor",
  "General / Multiple Roles",
];

export const CHANNEL_URL = "https://www.youtube.com/@ItsMelto";
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STATUS_STYLES = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  reviewing: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  accepted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rejected: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

export const ROLE_BADGE_STYLES = {
  "Standard Animator": "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  "High-Quality Animator": "bg-violet-500/15 text-violet-300 border-violet-500/30",
  "Production Manager": "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  "Script Writer": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Video Editor": "bg-teal-500/15 text-teal-300 border-teal-500/30",
  "General / Multiple Roles": "bg-slate-500/15 text-slate-300 border-slate-500/30",
  "Standard + HQ Animator": "bg-violet-500/15 text-violet-300 border-cyan-500/40",
  "Worker": "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const STYLE_REFERENCES = [
  "https://www.youtube.com/shorts/zyupoESQyhc",
  "https://www.youtube.com/shorts/hWGoaKt9jNQ",
  "https://www.youtube.com/shorts/e1j92Psdx_A",
  "https://www.youtube.com/shorts/xBmpslcLBZI",
  "https://www.youtube.com/shorts/3IH7aqev0GA",
];

const CHANNEL_EXAMPLES = [
  "https://www.youtube.com/shorts/0mVXuQB1rqs",
  "https://www.youtube.com/shorts/Wig6LhEWEDE",
  "https://youtube.com/shorts/oOMU0U_xRwk",
  "https://youtube.com/shorts/9EpEc3UM-pI",
  "https://youtube.com/shorts/vJsyWxJU-pw",
];

const toLinks = (urls, prefix) => urls.map((link, i) => ({ title: `${prefix} ${String(i + 1).padStart(2, "0")}`, link }));

export const ROLE_DETAILS = {
  "Standard Animator": {
    id: "standard-animator",
    category: "Animation",
    pay: "Starting at $55 USD per video",
    tagline: "Bring Roblox characters to life with clean animation, solid acting, and pacing that fits the story.",
    lookingFor: [
      "Solid character acting, expressions, and body movement",
      "Camera movement, framing, and scene composition",
      "Comedy timing and pacing locked to the final audio",
      "Polished 30–60s shorts in Blender, Maya, or Moon Animator",
      "Reliable delivery against the job deadline",
    ],
    creativeFreedom:
      "You're hired for your portfolio — no micromanaging. Follow the provided references and directed scenes closely; everything else is your own style and creative freedom.",
    styleReferences: toLinks(STYLE_REFERENCES, "Style reference"),
    channelExamples: toLinks(CHANNEL_EXAMPLES, "Channel example"),
    payRows: [
      { label: "Base rate", value: "Starting at $55 USD per video" },
      { label: "Scaling", value: "Pay may increase with length, complexity & requirements" },
      { label: "Every job", value: "Exact pay shown upfront" },
    ],
  },
  "High-Quality Animator": {
    id: "high-quality-animator",
    category: "Animation — Premium",
    pay: "Starting at $110 USD per video",
    tagline: "Top-tier cinematic animation — standout acting, camera work, and environments for the channel's biggest videos.",
    lookingFor: [
      "Exceptional character acting, facial expressions, and body movement",
      "Cinematic camera movement, framing, and scene composition",
      "Environments and set design that elevate the story",
      "Comedy timing and pacing locked to the final audio",
      "A portfolio that already matches or beats the channel's best work",
    ],
    creativeFreedom:
      "You're hired for your portfolio — no micromanaging. Follow the provided references and directed scenes closely; everything else is your own style and creative freedom. The goal is polished hooks, framing, camera movement, character acting, and pacing — not one copied house style.",
    styleReferences: toLinks(STYLE_REFERENCES, "Style reference"),
    channelExamples: toLinks(CHANNEL_EXAMPLES, "Channel example"),
    payRows: [
      { label: "Base rate", value: "Starting at $110 USD per video" },
      { label: "Scaling", value: "Pay may increase with length, complexity & requirements" },
      { label: "Every job", value: "Exact pay shown upfront" },
    ],
  },
  "Script Writer": {
    id: "script-story-writer",
    category: "Creative Writing",
    pay: "$10 USD / accepted script",
    tagline: "Write complete, ready-to-animate storylines with hooks people can't skip — built for short-form Roblox pacing.",
    lookingFor: [
      "Deep feel for Roblox culture, memes, and YouTube Shorts pacing",
      "Hook-first writing — grab viewers in the first 2 seconds",
      "A complete, usable storyline that can go straight into production",
      "Original, engaging comedy skits and gaming story arcs",
    ],
    creativeFreedom: null,
    styleReferences: null,
    channelExamples: toLinks(CHANNEL_EXAMPLES, "Channel example"),
    payRows: [
      { label: "Per accepted script", value: "$10 USD" },
      { label: "Requirement", value: "Complete, ready-to-animate storyline" },
      { label: "Not accepted?", value: "Free to rework & resubmit" },
    ],
  },
  "Video Editor": {
    id: "video-editor",
    category: "Post-Production",
    pay: "$20 USD / completed video",
    tagline: "Handle the final polish — animation and audio arrive finished, you deliver the upload-ready video.",
    lookingFor: [
      "Premiere Pro, DaVinci Resolve, or After Effects",
      "Clean captions, zooms, and punch-in timing",
      "Final color, framing polish, and export quality",
      "Fast, reliable turnaround — most edits take about an hour",
    ],
    creativeFreedom: null,
    styleReferences: null,
    channelExamples: toLinks(CHANNEL_EXAMPLES, "Channel example"),
    payRows: [
      { label: "Per completed video", value: "$20 USD" },
      { label: "What you receive", value: "Finished animation + final audio" },
      { label: "Your job", value: "Final edit → upload-ready video" },
    ],
  },
};

export const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("melto_token")}` });
export const fileUrl = (path) => `${API}/files/${path}?auth=${localStorage.getItem("melto_token")}`;

export const uploadAdminFile = async (file) => {
  const axios = (await import("axios")).default;
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/admin/upload`, form, { headers: authHeaders() });
  return data;
};

export const JOB_STATUS_STYLES = {
  draft: "bg-[var(--pv-raised)] text-[var(--pv-text-2)] border-dashed border-[var(--pv-border-strong)]",
  available: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  active: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  awaiting_review: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  in_revision: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

export const JOB_STATUS_LABELS = {
  draft: "Draft",
  available: "Available",
  active: "Active",
  awaiting_review: "Awaiting Review",
  in_revision: "In Revision",
  completed: "Completed",
};

export const QUALITY_STATUSES = {
  good_standing: { label: "Good Standing", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  needs_improvement: { label: "Needs Improvement", style: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  quality_warning: { label: "Quality Warning", style: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
  final_warning: { label: "Final Warning", style: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
};
